import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ZoneDocCoverageFormComponent,
  ZoneDocCoverage,
} from '../../zone-doc-coverage-form/zone-doc-coverage-form.component';
import {
  LicenseRequirement,
  LicenseZoneNode,
  licenseNodesInZone,
  licenseNodesUnderService,
} from '../zone-doc.util';

// Shared edit / link coverage dialog (no file picker/preview): the doc-chip
// pencil opens it prefilled with the document's current coverage + expiration.
// "Applies to requirement" is the single link control; coverage is DERIVED from
// the chosen requirement's node(s) in the zone rather than a free service
// dropdown. Below the requirement selector it presents the SAME UI as the upload
// dialog: a read-only Service context plus a checkbox per configured sub-service
// of the requirement under that service (the coverage form's service chip +
// "also apply to" checkboxes). The checkboxes are seeded from the document's
// CURRENT coverage (not all-checked) so an edit reflects what is on file — a
// multi-sub-type document keeps its full coverage and an expiration-only edit
// preserves its subs. A requirement spanning more than one service first asks
// which service the document covers (a document has one serviceType); once a
// service resolves, that service's configured-sub checkboxes render. "Not tied to
// a requirement"/no applicable requirements leaves coverage empty (an Additional
// document, dates only). On Save it emits the coverage the shared form describes;
// the host converts + PATCHes it via its own client (vendor → document.service,
// internal → admin.service), so this dialog stays data-access agnostic.
@Component({
  standalone: true,
  selector: 'zone-doc-edit-dialog',
  imports: [CommonModule, FormsModule, ZoneDocCoverageFormComponent],
  templateUrl: './zone-doc-edit-dialog.component.html',
  styleUrl: './zone-doc-edit-dialog.component.scss',
})
export class ZoneDocEditDialogComponent implements OnInit, OnChanges {
  // The host controls presence via *ngIf (fresh instance per open). Human-readable
  // zone label for the footer (host decodes the state enum).
  @Input() zoneLabel = '';
  // The document being edited (drives the header chip).
  @Input() document: any = null;
  // The service zone being edited + the serviceTypeId → type bridge. Together
  // with the offered licenses they let this dialog derive a requirement's
  // coverage node(s) in the zone.
  @Input() serviceZone: any = null;
  @Input() serviceTypeIdToType: Record<string, string> = {};
  // Prefill: the document's current coverage (used to pre-select the covered node
  // when the linked requirement resolves to more than one node) + expiration.
  @Input() initialServiceType: string | null = null;
  @Input() initialSubTypes: string[] = [];
  @Input() initialExpiration = '';
  @Input() initialIssueDate = '';
  // The offered license requirements the document can be attached to (the
  // distinct licenses producing >= 1 row in this zone). When non-empty the
  // "Applies to requirement" selector renders; when empty the dialog shows the
  // "no requirements apply" empty state and allows an expiration-only save. Each
  // entry supplies { id, documentTitle, issueDateRequired, expirationDateRequired }.
  @Input() selectableLicenses: LicenseRequirement[] = [];
  // Prefill: the document's current linked requirement (null = not tied to one).
  @Input() initialLicenseRequirementId: string | null = null;
  // Disables Save while the host's PATCH is in flight.
  @Input() submitting = false;

  // Emits the latest coverage on Save; the host validates + PATCHes it. May be
  // null if the coverage form has not emitted yet.
  @Output() save = new EventEmitter<ZoneDocCoverage | null>();
  @Output() cancelled = new EventEmitter<void>();

  // Bound to the "Applies to requirement" selector; seeded from the prefill and
  // fed into the coverage form so its emitted coverage carries the chosen link
  // (the coverage edit is a full replacement server-side).
  selectedLicenseRequirementId: string | null = null;

  // The selected requirement's coverage node(s) in the zone.
  licenseNodes: LicenseZoneNode[] = [];
  // The distinct services those nodes span. A single service auto-resolves; more
  // than one renders the "Applies to service" picker (a document has ONE
  // serviceType, so the user picks which service it covers).
  coverageServices: any[] = [];
  // The service the document is scoped to (one of `coverageServices`).
  selectedService: any = null;

  // Inputs fed to the shared coverage form, recomputed from the selected
  // requirement + service. `coverageService` is the always-covered read-only
  // service context; `coverageSiblings` carries this requirement's CONFIGURED
  // sub-services under that service as "also apply to" checkboxes, each seeded
  // from the document's current coverage — the same presentation as the upload
  // dialog — so a multi-sub-type document keeps (and can add/remove) its full
  // sub-type coverage instead of collapsing to one. The coverage form's own
  // `scopedSubType` is always null here (the edit dialog has no always-on scoped
  // sub — every sub is a checkbox reflecting what is on file).
  coverageService: any = null;
  coverageSiblings: any[] = [];
  // Whether the selected requirement has a service-level node (subType null) under
  // the resolved service — the edit-dialog analogue of the upload dialog's input.
  // When true a service-only coverage (no subs checked) satisfies the service-level
  // row; when false the requirement is sub-only under this service, so >= 1
  // configured sub must be checked or the save would orphan the document. Computed
  // per resolved service, so the multi-service picker recomputes it on selection.
  hasServiceLevelNode = false;

  // Latest coverage emitted by the shared coverage form child.
  private coverage: ZoneDocCoverage | null = null;

  ngOnInit(): void {
    this.seedSelectedLicense();
    this.recompute();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A reopen for a different document (or a change to the offered set) re-seeds
    // the link from the prefill — dropping a seed that no longer resolves to a row.
    if (changes['initialLicenseRequirementId'] || changes['selectableLicenses']) {
      this.seedSelectedLicense();
    }
    // Re-derive the covered node(s) when the link or the zone context changes.
    if (
      changes['initialLicenseRequirementId'] ||
      changes['selectableLicenses'] ||
      changes['serviceZone'] ||
      changes['serviceTypeIdToType'] ||
      changes['initialServiceType'] ||
      changes['initialSubTypes']
    ) {
      this.recompute();
    }
  }

  // Seed the "Applies to requirement" selector from the prefill, but only when the
  // seeded requirement is actually offered in this zone (present in
  // selectableLicenses → produces >= 1 row). A document linked to a license that
  // no longer produces a row here (service un-offered, or a state-specific license
  // after a zone state change) is treated as NOT tied, so it presents as a clean
  // Additional document the user can re-link — never a dangling link that resolves
  // to zero nodes and silently drops coverage on save.
  private seedSelectedLicense(): void {
    const initial = this.initialLicenseRequirementId ?? null;
    const offered =
      initial != null &&
      (this.selectableLicenses ?? []).some(
        (license: any) => String(license?.id) === String(initial),
      );
    this.selectedLicenseRequirementId = offered ? initial : null;
  }

  // The currently-selected license definition (drives the coverage form's date
  // flags), or null when "Not tied to a requirement" is chosen.
  get selectedLicense(): any {
    if (!this.selectedLicenseRequirementId) {
      return null;
    }
    return (
      (this.selectableLicenses ?? []).find(
        (license: any) =>
          String(license?.id) === String(this.selectedLicenseRequirementId),
      ) ?? null
    );
  }

  get expirationRequired(): boolean {
    return !!this.selectedLicense?.expirationDateRequired;
  }

  get issueRequired(): boolean {
    return !!this.selectedLicense?.issueDateRequired;
  }

  // Re-derive the selected requirement's coverage node(s) in the zone, the
  // distinct services they span, and the coverage-form inputs.
  private recompute(): void {
    this.licenseNodes = licenseNodesInZone(
      this.serviceZone,
      this.selectableLicenses,
      this.serviceTypeIdToType,
      this.selectedLicenseRequirementId,
    );
    this.coverageServices = this.distinctServices(this.licenseNodes);
    this.selectedService = this.pickService();
    this.rebuildCoverageInputs();
  }

  // The distinct services among the requirement's nodes, de-duplicated by `type`,
  // node order preserved.
  private distinctServices(nodes: LicenseZoneNode[]): any[] {
    const services: any[] = [];
    const seen = new Set<string>();
    for (const node of nodes) {
      const type = node.service?.type;
      if (type && !seen.has(type)) {
        seen.add(type);
        services.push(node.service);
      }
    }
    return services;
  }

  // No services → null; exactly one → auto-select it; more than one → pre-select
  // the service matching the document's current coverage, else leave unset so the
  // user must pick (a document covers a single service).
  private pickService(): any {
    if (this.coverageServices.length === 0) {
      return null;
    }
    if (this.coverageServices.length === 1) {
      return this.coverageServices[0];
    }
    return (
      this.coverageServices.find(
        (service) => service?.type === this.initialServiceType,
      ) ?? null
    );
  }

  // Recompute the coverage-form inputs from the selected service — the SAME
  // presentation as the upload dialog. The chosen service is the always-covered
  // read-only context; the requirement's CONFIGURED sub-services under that
  // service become "also apply to" checkboxes, each pre-checked from the
  // document's current coverage (only when the document's own serviceType matches
  // the service, so a multi-service doc never pre-checks another service's subs).
  // Seeding from the current coverage — rather than all-checked — is what keeps an
  // expiration-only edit from collapsing a multi-sub-type document (all its subs
  // stay checked and re-save) while still reflecting exactly what is on file.
  private rebuildCoverageInputs(): void {
    const service = this.selectedService;
    this.coverageService = service;
    if (!service) {
      this.coverageSiblings = [];
      this.hasServiceLevelNode = false;
      return;
    }
    const seedFromDoc =
      this.initialServiceType != null &&
      service?.type === this.initialServiceType;
    const nodesUnderService = licenseNodesUnderService(
      this.serviceZone,
      this.selectableLicenses,
      this.serviceTypeIdToType,
      this.selectedLicenseRequirementId,
      service,
    );
    // A service-level node (subType null) makes a service-only coverage valid.
    this.hasServiceLevelNode = nodesUnderService.some((node) => !node.subType);
    this.coverageSiblings = nodesUnderService
      .filter((node) => !!node.subType)
      .map((node) => ({
        type: node.subType.type,
        displayName: node.subType.displayName,
        checked:
          seedFromDoc && (this.initialSubTypes ?? []).includes(node.subType.type),
      }));
  }

  // The "Applies to requirement" selector changed — re-derive everything for the
  // new link. The coverage form re-emits when its inputs change.
  onLicenseSelect(): void {
    this.recompute();
  }

  // The "Applies to service" picker changed (multi-service requirement) — rebuild
  // the coverage-form inputs for the chosen service. The coverage form re-emits
  // from its updated [service]/[siblingSubTypes] inputs.
  onServiceSelect(): void {
    this.rebuildCoverageInputs();
  }

  // The shared coverage form emits on init and on every change; keep only the
  // latest so Save can read the current coverage without reaching in.
  onCoverageChange(coverage: ZoneDocCoverage): void {
    this.coverage = coverage;
  }

  // Whether the license's required date fields are satisfied by the latest
  // coverage. Mirrors zone-doc-upload-dialog's gate — missing required dates
  // block Save (and disable the button) here too.
  get requiredDatesSatisfied(): boolean {
    if (this.expirationRequired && !this.coverage?.expirationDate) {
      return false;
    }
    if (this.issueRequired && !this.coverage?.issueDate) {
      return false;
    }
    return true;
  }

  // A chosen requirement must resolve to a covered service before Save: a
  // single-service requirement auto-selects it; a multi-service requirement needs
  // the user to pick which service the document covers. "Not tied to a
  // requirement" / no applicable requirements needs no coverage (an
  // expiration-only Additional doc). Once a service resolves, a sub-only
  // requirement (no service-level node under it) additionally needs >= 1 sub
  // checked — a service-only coverage would satisfy no row and orphan the document.
  get coverageComplete(): boolean {
    if (!this.selectedLicenseRequirementId || this.licenseNodes.length === 0) {
      return true;
    }
    if (!this.coverageService) {
      return false;
    }
    if (!this.hasServiceLevelNode) {
      return (this.coverage?.serviceSubTypes?.length ?? 0) > 0;
    }
    return true;
  }

  // Whether Save is enabled: no submit in flight, any license-required dates are
  // present, and a chosen requirement has a resolved covered node.
  get canSave(): boolean {
    return (
      !this.submitting && this.requiredDatesSatisfied && this.coverageComplete
    );
  }

  onCancel(): void {
    this.coverage = null;
    this.cancelled.emit();
  }

  onSave(): void {
    if (!this.canSave) {
      return;
    }
    this.save.emit(this.coverage);
  }
}
